<template>
  <div class="product-chooser">
    <!-- Loading View -->
<div v-if="loading" class="loading-container">
  <div class="spinner"></div>
  <p class="intro">Loading your account info...</p>
</div>
<p v-if="gateMessage" class="status-msg">
  {{ gateMessage }}
</p>

       <!-- Show Product Options -->
    <div v-else-if="shouldShowProducts">
      <h1>Get Access to PathTracer</h1>
      <p class="intro">
        Choose a plan below to unlock transaction analysis tools powered by visuals and reports.
      </p>

      <!-- Existing credit balance line -->
      <p
        class="intro"
        v-if="creditBalance !== null && !isSubscriptionUser && !isInfiniteAccess"
      >
        You currently have <strong>{{ creditBalance }}</strong> credit{{ creditBalance !== 1 ? 's' : '' }} remaining.
      </p>

      <!-- NEW: Main Analysis credit pricing banner -->
      <div
        v-if="mainAnalysisPricingLoaded"
        class="pricing-summary"
      >
        <p class="intro">
          Typical transaction analysis costs
          <strong>{{ mainAnalysisNormalCostLabel }}</strong> credits 
          <span v-if="mainAnalysisAdvancedCostLabel">
      <!--      or <strong>{{ mainAnalysisAdvancedCostLabel }}</strong> in Advanced mode-->
          </span>.
        </p>

        <p v-if="mainAnalysisDiscountLabel" class="discount-msg">
          {{ mainAnalysisDiscountLabel }}
        </p>
      </div>

      <!-- Optional: show an error if pricing could not be loaded -->
      <p v-if="mainAnalysisPricingError" class="status-msg">
        {{ mainAnalysisPricingError }}
      </p>

      <p
        v-if="atMaxSubCap"
        class="status-msg"
      >
        You’re at the maximum prepaid subscription length ({{ maxSubMonths }} months). Try again closer to your expiry.
      </p>

      <div v-if="subscriptionExpiresAt && !isSubscriptionUser">
        <p class="status-msg">
          ⏳ Your subscription expired on <strong>{{ formatDate(subscriptionExpiresAt) }}</strong>.
        </p>
      </div>

      <div class="product-card" v-for="product in filteredProducts" :key="product.id">
        <h2>{{ product.name }}</h2>
        <p class="price">{{ product.price }}</p>
        <p class="description">{{ product.description }}</p>
        <div class="button-wrapper">
<button
  @click="guardedCheckout(product)"
  :disabled="(product.id==='subscription' && atMaxSubCap) || gateLoading || checkoutRedirecting"
  :title="product.id==='subscription' && atMaxSubCap ? `You can only hold ${maxSubMonths} months at a time.` : ''"
  :data-track="'product_checkout_click'"
  :data-track-props="JSON.stringify({ priceId: product.priceId, productId: product.id })"
>
  <span v-if="gateLoading && !checkoutRedirecting">One moment please...</span>
  <span v-else-if="checkoutRedirecting">Redirecting to Stripe...</span>
  <span v-else>{{ getButtonLabel(product) }}</span>
</button>


        </div>
        <p> </p>
    <small v-if="product.id==='subscription'" class="hint">
  <center>You can hold up to {{ maxSubMonths }} months of prepaid time.</center>
</small>
      </div>

      <p v-if="status" class="status-msg">{{ status }}</p>
    </div>

    <!-- Already Subscribed / Infinite Access View -->
    <div v-else>
      <img
        src="https://cdn-icons-png.flaticon.com/512/190/190411.png"
        alt="Checkmark"
        style="width: 64px; margin: 20px auto;"
      />

      <div v-if="isSubscriptionUser">
        <h1>You're on a Subscription</h1>
        <p class="intro">
          You already have full access to PathTracer tools.<br />
          Your subscription expires in <strong>{{ subscriptionDays }}</strong> day{{ subscriptionDays !== 1 ? 's' : '' }}.
          You can extend your subscription below.
        </p>
      </div>

      <div v-else-if="hasExpiredSubscription">
        <h1>Subscription Expired</h1>
        <p class="intro">
          Your subscription expired on <strong>{{ formatDate(subscriptionExpiresAt) }}</strong>.<br />
          You can either renew your subscription or purchase credits to continue using PathTracer.
        </p>
      </div>

      <div v-else>
        <h1>No Purchase Needed</h1>
        <p class="intro">
          You already have full access to all PathTracer tools.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

import { logEvent } from '@/analytics'; // ← add this

const MAX_SUB_MONTHS = 6;

function monthsBetweenInclusive(now, future) {
  // Count whole months between "now" and "future" (floor)
  const a = new Date(now.getFullYear(), now.getMonth(), 1);
  const b = new Date(future.getFullYear(), future.getMonth(), 1);
  const months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  return Math.max(0, months);
}


export default {
  name: 'StripeProductChooser',
  data() {
    return {
    checkoutRedirecting: false,   // ← NEW
       // NEW: cached Main Analysis pricing (Normal + Advanced)
      mainAnalysisNormalPricing: null,    // mode "bucket"
      mainAnalysisAdvancedPricing: null,  // mode "normal"
      mainAnalysisPricingError: '',
      mainAnalysisPricingLoading: false,
   gateStatus: null,      // e.g. 'approved', 'soft_block_vpn', 'hard_block_region'
    gateMessage: '',       // last message from gate endpoint
    gateLoading: false,
      status: '',
      maxSubMonths: MAX_SUB_MONTHS,
      creditBalance: null,
      isSubscriptionUser: false,
      isUnlimitedUser: false,
    subscriptionExpiresAt: null,
      role: null,
      subscriptionDays: null,
    loading: true, 
      hasLoggedImpression: new Set(),   // ← track per priceId
      io: null,                         // ← IntersectionObserver
    products: [
    
  {
    id: 'credits',
    name: 'PathTracer Credits Package (25 Credits)',
    price: '$25',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SXUJiFoYPmMTGQ9V1PGPT7v' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (50 Credits)',
    price: '$50',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SVzJSFoYPmMTGQ9zNhkVLUn' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (100 Credits)',
    price: '$100',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SVb8HFoYPmMTGQ9YRYDR9vp' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (250 Credits)',
    price: '$250',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SVzKdFoYPmMTGQ9vKqDvudL' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (500 Credits)',
    price: '$400',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SVzOCFoYPmMTGQ9cmUOxTHA' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (1000 Credits)',
    price: '$750',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SVzPdFoYPmMTGQ9xLNKjqFF' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (2500 Credits)',
    price: '$1500',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SVzTWFoYPmMTGQ9ASaYw8nr' 
  },
  
  
]

    };
  },

   mounted() {
    // Observe product card impressions (once per priceId)
    this.$nextTick(() => {
      const opts = { root: null, rootMargin: '0px', threshold: 0.25 };
      this.io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target;
          const priceId = el.getAttribute('data-price-id');
          if (!priceId || this.hasLoggedImpression.has(priceId)) continue;

          const product = this.products.find(p => p.priceId === priceId);
          if (product) {
            logEvent('product_list_impression', {
              id: product.id,
              priceId: product.priceId,
              name: product.name,
              price: product.price
            });
            this.hasLoggedImpression.add(priceId);
          }
        }
      }, opts);
 // NEW: ensure fresh reload when restored from bfcache (Back from Stripe etc.)
    this._onPageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', this._onPageShow);

      // Attach observer to currently rendered cards
      this.$el.querySelectorAll('.product-card').forEach(card => this.io.observe(card));
    });
  },
  beforeUnmount() {
    if (this.io) { this.io.disconnect(); this.io = null; }

      if (this._onPageShow) {
      window.removeEventListener('pageshow', this._onPageShow);
    }
  },

  computed: {
 // NEW: Pricing banner helpers
    mainAnalysisPricingLoaded() {
      return !!(this.mainAnalysisNormalPricing || this.mainAnalysisAdvancedPricing);
    },

    mainAnalysisNormalCostLabel() {
      const p = this.mainAnalysisNormalPricing;
      if (!p) return '';
      return this.resolveDisplayCost(p);
    },

    mainAnalysisAdvancedCostLabel() {
      const p = this.mainAnalysisAdvancedPricing;
      if (!p) return '';
      return this.resolveDisplayCost(p);
    },

    mainAnalysisDiscountLabel() {
      // Prefer any active discount label from either pricing object
      const p = this.mainAnalysisNormalPricing || this.mainAnalysisAdvancedPricing;
      if (!p) return '';

      const now = p.serverNowUtc ? new Date(p.serverNowUtc) : null;
      if (!p.discountLabel) return '';

      if (
        p.discountCost != null &&
        p.discountStartsUtc &&
        p.discountEndsUtc &&
        now
      ) {
        const start = new Date(p.discountStartsUtc);
        const end = new Date(p.discountEndsUtc);
        if (now >= start && now <= end) {
          return p.discountLabel;
        }
      }

      return '';
    },
     monthsAheadBooked() {
    if (!this.subscriptionExpiresAt) return 0;
    const now = new Date();
    const expiry = new Date(this.subscriptionExpiresAt);
    if (expiry <= now) return 0;
    return monthsBetweenInclusive(now, expiry);
  },

  // How many months are still allowed to purchase before reaching the cap
  monthsRemainingAllowance() {
    const remaining = MAX_SUB_MONTHS - this.monthsAheadBooked;
    return Math.max(0, remaining);
  },

  // Is the user already at/over the cap?
  atMaxSubCap() {
    return this.monthsRemainingAllowance <= 0;
  },

  // Should subscription be shown as a purchasable product?
  // - If at cap → hide it (or you can show it disabled; see below)
  filteredProducts() {
    if (this.isInfiniteAccess) return [];

    const base = (() => {
      if (this.hasExpiredSubscription) return this.products;             // renew or credits
      if (this.isSubscriptionUser) return this.products.filter(p => p.id === 'subscription');
      return this.products;
    })();

    // Hide subscription when at cap
    return base.filter(p => !(p.id === 'subscription' && this.atMaxSubCap));
  },
  hasExpiredSubscription() {
    if (!this.subscriptionExpiresAt) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(this.subscriptionExpiresAt);
    expiry.setHours(0, 0, 0, 0);
    return expiry < today;
  },

    isInfiniteAccess() {
      return this.role === 'admin' || this.role === 'moderator' || this.isUnlimitedUser;
    },
    shouldShowProducts() {
      // Only show products if the user doesn't have unlimited access
      return !this.isInfiniteAccess;
    },



  },
   created() {
    logEvent('product_page_view', { testMode: true });

    const token = localStorage.getItem('token');
    if (!token) {
      this.status = 'You must be logged in.';
      this.loading = false;
      return;
    }

    axios.get('/etherscan/get_credits.php', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        this.loading = false;
        if (res.data.status === 'success') {
          this.creditBalance = res.data.balance;
          this.subscriptionExpiresAt = res.data.subscription_expires_at;

          const expires = this.subscriptionExpiresAt ? new Date(this.subscriptionExpiresAt) : null;
          const isExpired = !expires || expires <= new Date();

          this.isSubscriptionUser = !!res.data.is_subscription_user && !isExpired;
          this.isUnlimitedUser = !!res.data.is_unlimited_user;
          this.role = res.data.role;
          this.subscriptionDays = res.data.subscriptionDays || 0;

          logEvent('product_account_status_loaded', {
            balance: this.creditBalance,
            isSubscriptionUser: this.isSubscriptionUser,
            isUnlimitedUser: this.isUnlimitedUser,
            role: this.role,
            subscriptionDays: this.subscriptionDays,
            subscriptionExpiresAt: this.subscriptionExpiresAt,
            hasExpiredSubscription: this.hasExpiredSubscription
          });

        } else {
          this.status = 'Failed to load account status.';
        }
      })
      .catch(err => {
        this.loading = false;
        console.error('Error fetching account data:', err);
        this.status = 'Error checking your account access.';
      })
      .then(() => {
        // Always attempt to load pricing if we have a token
        this.fetchMainAnalysisPricing();
      });
  },

  methods: {

async checkPurchaseEligibility() {
  this.gateMessage = '';

  const token = localStorage.getItem('token');
  if (!token) {
    this.gateStatus = 'error';
    this.gateMessage = 'You must be logged in to purchase.';
    return { ok: false };
  }

  try {
    const res = await axios.post(
      '/etherscan/other/check_purchase_gate.php',
      {},
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    const payload = res.data || {};

    // Hard error from endpoint (status !== success)
    if (payload.status !== 'success') {
      this.gateStatus = 'error';
      this.gateMessage = payload.message || 'Unable to verify purchase eligibility.';
      return { ok: false, payload };
    }

    const ok = !!payload.can_proceed;
    this.gateStatus = payload.overall_status || null;

    if (!ok) {
      // Block / soft-block -> show message to user
      this.gateMessage = payload.message || 'You are not currently eligible to purchase.';
    } else {
      // Success -> do NOT show persistent banner
      this.gateMessage = '';
    }

    return { ok, payload };
  } catch (err) {
    console.error('Purchase gate error:', err);

    const res = err?.response;
    const serverPayload = res?.data;

    if (serverPayload) {
      console.error('Purchase gate server error payload:', serverPayload);
      if (serverPayload.debug) {
        console.warn('Purchase gate debug:', serverPayload.debug);
      }
    }

    this.gateStatus = 'error';
    this.gateMessage =
      (serverPayload && serverPayload.message) ||
      'Error - please try again shortly or contact support. ' + (err?.message || '');

    return { ok: false };
  }
}

,
async guardedCheckout(product) {
  logEvent('product_checkout_click', {
    priceId: product.priceId,
    productId: product.id
  });

  if (product.id === 'subscription' && this.atMaxSubCap) {
    this.status = `You’re already prepaid ${this.maxSubMonths} months out.`;
    return;
  }

  // Optional: skip gate for internal roles (no fancy UX needed)
  if (this.isUnlimitedUser || this.role === 'admin' || this.role === 'moderator') {
    return this.checkout(product);
  }

  // Phase 1: “One moment please…” while running gate
  const minGateDelayMs = 500;
  const gateStart = Date.now();
  this.gateLoading = true;
  this.checkoutRedirecting = false;
  this.status = '';

  const result = await this.checkPurchaseEligibility();

  const gateElapsed = Date.now() - gateStart;
  if (gateElapsed < minGateDelayMs) {
    await new Promise(resolve => setTimeout(resolve, minGateDelayMs - gateElapsed));
  }
  this.gateLoading = false;

  if (!result.ok) {
    // Gate blocked, use its user-facing message
    this.status = this.gateMessage || 'You are not currently eligible to purchase.';
    logEvent('product_purchase_gate_block', {
      productId: product.id,
      priceId: product.priceId,
      gateStatus: this.gateStatus || null
    });
    return;
  }

  // Phase 2: “Redirecting to Stripe…” with a short pause
  this.status = 'Redirecting to Stripe...';
  this.checkoutRedirecting = true;

  const minRedirectDelayMs = 1000;
  await new Promise(resolve => setTimeout(resolve, minRedirectDelayMs));

  await this.checkout(product);
}
,


       // NEW: normalize pricing to a human label
    resolveDisplayCost(pricing) {
      const base = Number(pricing.baseCost || 0);
      if (!base) return '';

      const now = pricing.serverNowUtc ? new Date(pricing.serverNowUtc) : null;
      const hasDiscountWindow =
        pricing.discountCost != null &&
        pricing.discountStartsUtc &&
        pricing.discountEndsUtc &&
        now;

      if (
        hasDiscountWindow &&
        now >= new Date(pricing.discountStartsUtc) &&
        now <= new Date(pricing.discountEndsUtc) &&
        pricing.discountCost < base
      ) {
        // Example: "4 (normally 6)"
        return `${pricing.discountCost} (normally ${base})`;
      }

      return String(base);
    },

    // NEW: fetch Main Analysis pricing for Normal + Advanced modes
    async fetchMainAnalysisPricing() {
      this.mainAnalysisPricingError = '';
      this.mainAnalysisPricingLoading = true;

      const token = localStorage.getItem('token');
      if (!token) {
        this.mainAnalysisPricingLoading = false;
        return;
      }

      const fetchForMode = async (mode) => {
        const res = await axios.get('/etherscan/fetch_platform_settings.php', {
          params: {
            context: 'main_analysis',
            mode
          },
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = res.data || {};
        if (data.status !== 'success' || !data.pricing) {
          throw new Error(data.message || 'Missing pricing data');
        }

        const p = data.pricing || {};

        const parsed = {
          baseCost: Number(p.base_cost ?? p.baseCredits ?? 0),
          discountCost:
            p.discount_cost != null
              ? Number(p.discount_cost)
              : p.discountCredits != null
              ? Number(p.discountCredits)
              : null,
          discountLabel: p.discount_label || p.discountLabel || '',
          discountStartsUtc: p.discount_starts_utc || p.discountStartsUtc || null,
          discountEndsUtc: p.discount_ends_utc || p.discountEndsUtc || null,
          serverNowUtc: data.server_now_utc || data.serverNowUtc || null
        };

        if (!parsed.baseCost || !parsed.serverNowUtc) {
          throw new Error('Incomplete pricing response');
        }

        // Sanity check discount
        if (parsed.discountCost != null && parsed.discountCost >= parsed.baseCost) {
          parsed.discountCost = null;
        }

        return parsed;
      };

      try {
        // "bucket" = Normal mode, "normal" = Advanced mode (matching your main tool)
        const [bucketPricing, normalPricing] = await Promise.all([
          fetchForMode('bucket'),
          fetchForMode('normal')
        ]);

        this.mainAnalysisNormalPricing = bucketPricing;
        this.mainAnalysisAdvancedPricing = normalPricing;

        logEvent('product_analysis_pricing_loaded', {
          normalBase: bucketPricing?.baseCost ?? null,
          advancedBase: normalPricing?.baseCost ?? null
        });
      } catch (err) {
        console.error('Failed to load main analysis pricing:', err);
        this.mainAnalysisPricingError =
          'Could not fetch current analysis credit costs. They will still be shown before you run an analysis.';
      } finally {
        this.mainAnalysisPricingLoading = false;
      }
    },

  async checkout(product) {
  logEvent('product_checkout_click', { priceId: product.priceId, productId: product.id });

  if (product.id === 'subscription' && this.atMaxSubCap) {
    this.status = `You’re already prepaid ${this.maxSubMonths} months out.`;
    this.checkoutRedirecting = false;
    return;
  }

  // status is already "Redirecting to Stripe..." from guardedCheckout
  try {
    const res = await axios.post(
      '/stripe/create_checkout.php',
      { priceId: product.priceId },
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    );

    if (res.data.url) {
      logEvent('product_checkout_redirect_ok', {
        priceId: product.priceId,
        productId: product.id
      });
      window.location.href = res.data.url;
    } else {
      this.status = 'Checkout failed. Try again.';
      this.checkoutRedirecting = false;
      logEvent('product_checkout_redirect_error', {
        priceId: product.priceId,
        productId: product.id,
        message: 'missing_url'
      });
    }
  } catch (err) {
    console.error('Checkout error:', err);
    this.status = 'Error launching checkout.';
    this.checkoutRedirecting = false;
    logEvent('product_checkout_redirect_error', {
      priceId: product.priceId,
      productId: product.id,
      message: String(err?.message || err)
    });
  }
}
,
  getButtonLabel(product) {
    if (product.id === 'subscription') {
      if (this.atMaxSubCap) return 'Max extension reached';
      if (this.subscriptionExpiresAt && !this.isSubscriptionUser) return 'Renew Subscription';
      if (this.isSubscriptionUser) return 'Extend Subscription';
      return 'Buy Subscription';
    }
    return 'Buy Now';
  }
,
formatDate(isoString) {
  if (!isoString) return 'N/A';
  return new Date(isoString).toLocaleString();
}
,
     

  }
};
</script>

<style scoped>
.product-chooser {
  max-width: 900px;
  margin: 0 auto;
  padding: 30px;
  text-align: center;
  background: linear-gradient(180deg, #f3f6fb 0%, #ffffff 100%);
  border-radius: 12px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
}

h1 {
  font-size: 28px;
  margin-bottom: 10px;
  color: #2c3e50;
}

.intro {
  font-size: 17px;
  color: #555;
  margin-bottom: 30px;
}

.product-card {
  background: #fff;
  border-radius: 12px;
  padding: 25px;
  margin: 20px auto;
  max-width: 500px;
  box-shadow: 0 6px 12px rgba(103, 114, 229, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  text-align: left;
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(103, 114, 229, 0.25);
}

.product-card h2 {
  font-size: 20px;
  color: #34495e;
  margin-bottom: 10px;
}

.price {
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 8px;
}

.description {
  font-size: 14px;
  color: #555;
  margin-bottom: 16px;
}

button {
  background: linear-gradient(135deg, #6772e5, #5469d4);
  color: white;
  padding: 12px 24px;
  font-size: 15px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease;
}

button:hover {
  background: linear-gradient(135deg, #5a63e0, #4a58cc);
  box-shadow: 0 4px 10px rgba(84, 105, 212, 0.3);
}

.status-msg {
  margin-top: 30px;
  font-weight: bold;
  color: #e67e22;
}
.button-wrapper {
  display: flex;
  justify-content: center;
}
@media (max-width: 600px) {
  .product-card {
    padding: 18px;
  }


  button {
    width: 100%;
  }
}
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 6px solid #ccc;
  border-top-color: #5469d4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.hint { display:block; margin-top:6px; color:#777; font-size:12px; }
button:disabled { opacity: .6; cursor: not-allowed; }
.pricing-summary {
  margin-bottom: 20px;
}

.discount-msg {
  font-size: 14px;
  color: #2c3e50;
}



</style>
